"use client";

import Image from "next/image";
import Link from "next/link";
import { AutoBreadcrumbs } from "../Breadcrumbs/AutoBreadcrumbs";
import styles from "./productOrbitHero.module.css";

export type OrbitProduct = {
  id: string;
  title: string;
  image: string;
  imageAlt: string;
  href: string;
  cover?: boolean;
  /** Fill more of the glass card (SpeechSync / Speeching Cards). */
  large?: boolean;
};

const DEFAULT_PRODUCTS: OrbitProduct[] = [
  {
    id: "speechsync",
    title: "SpeechSync AAC",
    image: "/products/tab-hero.webp",
    imageAlt: "SpeechSync AAC app on tablet",
    href: "/products/speechsync",
    large: true,
  },
  {
    id: "speeching-cards",
    title: "Speeching Cards",
    image: "/speechingcards.png",
    imageAlt: "Speeching Cards learning set",
    href: "/products/speeching-cards",
    large: true,
  },
  {
    id: "sogpod",
    title: "SOG Pod",
    image: "/SOGpod.svg",
    imageAlt: "SOG Pod multi-sensory device",
    href: "/products/sogpod",
  },
  {
    id: "neurocular",
    title: "Neurocular",
    image: "/products/neurocular/fingertrack.png",
    imageAlt: "Neurocular visual development game",
    href: "/products/neurocular",
    cover: true,
  },
];

const slotClass = [styles.slot0, styles.slot1, styles.slot2, styles.slot3];
const floatClass = [styles.float0, styles.float1, styles.float2, styles.float3];

type ProductOrbitHeroProps = {
  products?: OrbitProduct[];
  eyebrow?: string;
  description?: string;
};

export default function ProductOrbitHero({
  products = DEFAULT_PRODUCTS,
  eyebrow = "The Speech Clinic Dubai",
  description = "Explore our suite of products designed to support therapy, improve learning, and help children grow.",
}: ProductOrbitHeroProps) {
  const orbitProducts = products.slice(0, 4);

  return (
    <section className={styles.hero} aria-labelledby="products-hero-heading">
      <div className={styles.backdrop} aria-hidden>
        <span className={`${styles.glow} ${styles.glowA}`} />
        <span className={`${styles.glow} ${styles.glowB}`} />
      </div>

      <div className={styles.inner}>
        <div className={styles.crumbs}>
          <AutoBreadcrumbs variant="light" />
        </div>

        <div className={styles.copy}>
          <p className={styles.eyebrow}>{eyebrow}</p>
          <h1 id="products-hero-heading" className={styles.headline}>
            <span className={styles.headlineLead}>
              Powerful Products Built for
            </span>{" "}
            <span className={styles.headlineAccent}>
              Communication and Growth
            </span>
          </h1>
          <p className={styles.description}>{description}</p>
        </div>

        <div className={styles.stage}>
          <div className={styles.scene}>
            <div className={styles.orbitRing} aria-hidden />

            <div className={styles.hub} aria-hidden>
              <span className={styles.hubGlow} />
              <Image
                src="/speechcliniclogo.svg"
                alt=""
                width={120}
                height={120}
                className={styles.hubLogo}
              />
            </div>

            <div className={styles.rig}>
              {orbitProducts.map((product, index) => (
                <div
                  key={product.id}
                  className={`${styles.slot} ${slotClass[index]}`}
                >
                  <div className={styles.face}>
                    <Link
                      href={product.href}
                      className={styles.card}
                      aria-label={`View ${product.title}`}
                    >
                      <div className={`${styles.float} ${floatClass[index]}`}>
                        <div className={styles.pulse}>
                          <div
                            className={`${styles.visual} ${
                              product.cover ? styles.cover : ""
                            } ${product.large ? styles.large : ""}`}
                          >
                            <Image
                              src={product.image}
                              alt={product.imageAlt}
                              width={220}
                              height={220}
                              priority={index < 2}
                            />
                          </div>
                          <span className={styles.label}>{product.title}</span>
                        </div>
                      </div>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <svg
        className={styles.wave}
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        aria-hidden
      >
        <path
          fill="currentColor"
          d="M0,64 C240,120 480,0 720,40 C960,80 1200,120 1440,48 L1440,120 L0,120 Z"
        />
      </svg>
    </section>
  );
}
